import styled, { css, keyframes } from 'styled-components';

const PRIMARY = '#e71037';

interface ContainerProps {
  isFocused: boolean;
  disabled?: boolean;
  error?: boolean;
}

export const Container = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  color: #4a4b4a;
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  label {
    font-size: 12px;
    color: ${({ error, theme: { colors } }) =>
      error ? colors.error : '#757575'};
    ${({ isFocused }) =>
      isFocused &&
      css`
        color: ${PRIMARY};
      `}
  }

  > button {
    border-bottom: 1px solid
      ${({ error, theme: { colors } }) => (error ? colors.error : '#afafaf')};
    ${({ isFocused }) =>
      isFocused &&
      css`
        border-bottom-color: ${PRIMARY};
      `}
  }
`;

export const Trigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 0 0 2px;
  border: 0;
  background: transparent;
  font-family: inherit;
  font-size: 16px;
  line-height: 24px;
  color: #4a4b4a;
  text-align: left;
  cursor: pointer;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .placeholder {
    color: #afafaf;
  }

  svg {
    flex-shrink: 0;
  }

  :focus-visible {
    outline: none;
    border-bottom-color: ${PRIMARY};
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const popIn = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.4);
  animation: ${fadeIn} 0.2s ease-out;
`;

export const Sheet = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 480px;
  max-height: min(560px, 80vh);
  padding: 20px 20px 8px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.15);
  font-family: 'Open Sans', sans-serif;
  animation: ${popIn} 0.2s ease-out;
`;

export const SheetHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;

  strong {
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
  }

  button {
    display: flex;
    padding: 4px;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: #4a4b4a;
    cursor: pointer;

    svg {
      width: 20px;
      height: 20px;
    }

    :hover {
      background: #f1f1f1;
    }
  }
`;

export const Search = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #757575;

  :focus-within {
    border-color: ${PRIMARY};
  }

  input {
    flex: 1;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    font-family: inherit;
    font-size: 14px;
    color: #4a4b4a;

    &::placeholder {
      color: #afafaf;
    }
  }
`;

export const Options = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  margin: 0 -20px;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e4003a;
  }
`;

export const Option = styled.button<{ selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 48px;
  padding: 12px 20px;
  border: 0;
  border-bottom: 1px solid #f1f1f1;
  background: ${({ selected }) => (selected ? '#fdf0f2' : 'transparent')};
  font-family: inherit;
  font-size: 14px;
  font-weight: ${({ selected }) => (selected ? 600 : 400)};
  color: ${({ selected }) => (selected ? PRIMARY : '#4a4b4a')};
  text-align: left;
  cursor: pointer;

  :hover {
    background: ${({ selected }) => (selected ? '#fdf0f2' : '#f9f9f9')};
  }

  svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
  }
`;

export const Empty = styled.div`
  padding: 16px 20px;
  font-size: 14px;
  color: #757575;
`;

export const ErrorMessage = styled.span`
  position: absolute;
  bottom: -18px;
  color: ${({ theme }) => theme.colors.error};
  font-size: 12px;
`;
